import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { StorageEnum } from '~/utils/enum';
import { handleGetAuthUserProfile, handleLogin } from './authThunk';
import { IAuthUser } from '~/utils/interface/auth';
import { IUser } from '~/utils/interface/user';

export interface AuthState {
  accessToken: string | null | undefined;
  refreshToken?: string | null | undefined;
  roleScreen: string[];
  permissions: string[];
  username: string;
  name: string;
  avatarUrl: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  childUsers: [];
  locationIds: [];
  date_of_birth: string;
  deviceIds: [];
  status: number;
  isLoading: boolean;
  tokenDeviceFcm: string | null;
  isRequestNotifyLocation: boolean;
  needRefreshData: boolean;

  authUser: IAuthUser;
  userProfile: IUser;
  selectedUser: IUser;
}

const storedSelectedUser = localStorage.getItem(StorageEnum.SELECTED_USER);
const initialSelectedUser = storedSelectedUser ? JSON.parse(storedSelectedUser) : {};

const initialState: AuthState = {
  accessToken: '',
  refreshToken: '',
  isLoading: false,
  roleScreen: [],
  permissions: [],
  username: '',
  name: '',
  avatarUrl: '',
  email: '',
  phone: '',
  address: '',
  description: '',
  childUsers: [],
  locationIds: [],
  date_of_birth: '',
  deviceIds: [],
  status: 0,
  tokenDeviceFcm: '',
  isRequestNotifyLocation: false,
  needRefreshData: false,
  authUser: {},
  userProfile: {},
  selectedUser: initialSelectedUser,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    handleLogout(state) {
      localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
      localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
      state.accessToken = '';
    },
    setAccessToken(state, action: PayloadAction<string>) {
      state.accessToken = action.payload;
    },
    setSelectedUser(state, action: PayloadAction<IUser>) {
      state.selectedUser = action.payload;
    },
    changeTokenDeviceFcm(state, action: PayloadAction<string>) {
      state.tokenDeviceFcm = action.payload;
    },

    changeIsRequestNotifyLocation(state, action: PayloadAction<boolean>) {
      state.isRequestNotifyLocation = action.payload;
    },
    changeLogOutAuth(state, action: PayloadAction<string>) {
      state.accessToken = '';
      state.tokenDeviceFcm = '';
      state.refreshToken = '';
    },
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
    resetAuthState: (state) => {
      state.userProfile = {};
      state.selectedUser = {};
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleLogin.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(handleLogin.fulfilled, (state, action) => {
        state.accessToken = action.payload.access_token;
        state.refreshToken = action.payload.refresh_token;
        state.roleScreen = action.payload.role_screens ?? [];
        state.permissions = action.payload.permissions ?? [];
        state.username = action.payload.username ?? '';
        state.name = action.payload.name ?? '';
        state.avatarUrl = action.payload.avatar_url ?? '';
        state.email = action.payload.email ?? '';
        state.phone = action.payload.phone ?? '';
        state.address = action.payload.address ?? '';
        state.description = action.payload.description ?? '';
        state.childUsers = action.payload.child_users ?? [];
        state.locationIds = action.payload.location_ids ?? [];
        state.deviceIds = action.payload.device_ids ?? [];
        state.status = action.payload.status ?? 0;
        state.date_of_birth = action.payload.date_of_birth ?? '';
        state.isLoading = false;
      });
    builder
      .addCase(handleGetAuthUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(handleGetAuthUserProfile.fulfilled, (state, action) => {
        state.userProfile = action.payload;
        state.isLoading = false;
      })
      .addCase(handleGetAuthUserProfile.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const authActions = authSlice.actions;

const authReducer = authSlice.reducer;
export default authReducer;
